/**
 * Collection Groups Manager
 * Handles multi-group functionality for collections with age groups or categories
 */
class CollectionGroupsManager {
  constructor() {
    console.log('🏗️ CollectionGroupsManager constructor starting');
    console.log('🔍 window.collectionGroupData:', window.collectionGroupData);
    
    this.hasGroups = window.collectionGroupData?.hasGroups || false;
    this.groups = window.collectionGroupData?.groups || [];
    this.groupDisplayNames = window.collectionGroupData?.groupDisplayNames || [];
    this.groupDisplayTerm = window.collectionGroupData?.groupDisplayTerm || 'Category';
    this.selectedGroup = null;
    this.currentKit = null; // New: current athlete kit
    this.storageKey = 'collection_selected_group'; // Keep for backwards compatibility
    this.kitsStorageKey = 'athlete_kits'; // New: enhanced storage
    
    // Get current collection for validation (not for key scoping)
    this.currentCollection = this.getCurrentCollectionHandle();
    console.log('🏪 Current collection for validation:', this.currentCollection);
    console.log('🏪 Has groups:', this.hasGroups);
    
    if (this.hasGroups) {
      console.log('✅ Has groups - calling init()');
      this.init();
    } else {
      // Even if no groups, set up cart interception for svsCustomer properties
      console.log('🔧 No groups detected, but setting up svsCustomer cart interception anyway');
      this.setupSvsCustomerCartInterception();
    }
  }

  async init() {
    console.log('CollectionGroupsManager initializing with groups:', this.groups);
    
    // Store product details for later cart use
    this.storeProductDetails();
    
    // Set up cart interception for svsCustomer properties early
    this.setupSvsCustomerCartInterception();
    
    this.setupModal();
    this.setupGroupSelector();
    
    // Check if we need to wait for customer data BEFORE loading stored data
    await this.handleInitialGroupSelection();
    
    // handleInitialGroupSelection now handles all the filtering and URL checking
  }

  loadStoredData() {
    // First, try to load enhanced kit data with collection validation
    try {
      const kitsData = localStorage.getItem(this.kitsStorageKey);
      if (kitsData) {
        const parsed = JSON.parse(kitsData);
        
        // Validate collection context
        if (this.isValidForCurrentCollection(parsed)) {
          this.currentKit = parsed.currentKit;
          this.persistedSvsCustomerId = parsed.svsCustomerId; // Load persisted svsCustomerId
          
          // Convert kit data to selectedGroup format for backwards compatibility
          if (this.currentKit) {
            this.selectedGroup = {
              groupKey: this.currentKit.groupKey,
              groupName: this.currentKit.groupName
            };
            console.log('✅ Loaded valid kit data for current collection:', this.currentKit);
            console.log('✅ Loaded persisted svsCustomerId:', this.persistedSvsCustomerId);
            return;
          }
        } else {
          console.log('🧹 Clearing invalid kit data from different collection');
          localStorage.removeItem(this.kitsStorageKey);
        }
      }
    } catch (e) {
      console.warn('Error loading enhanced kit data:', e);
    }

    // Fallback: Load legacy selectedGroup data
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (stored) {
        const parsedGroup = JSON.parse(stored);
        
        // Check if legacy data has collection context (newer legacy data)
        if (parsedGroup.collection && parsedGroup.collection === this.currentCollection) {
          this.selectedGroup = parsedGroup;
          this.persistedSvsCustomerId = this.selectedGroup.svsCustomerId; // Load from legacy data
          
          // Convert legacy data to kit format
          this.currentKit = {
            id: 'kit_' + Date.now(),
            groupKey: this.selectedGroup.groupKey,
            groupName: this.selectedGroup.groupName,
            startedAt: Date.now(),
            complete: false
          };
          
          // Save in new format
          this.saveKitData();
          console.log('✅ Converted valid legacy data to kit format:', this.currentKit);
          console.log('✅ Loaded persisted svsCustomerId from legacy:', this.persistedSvsCustomerId);
        } else {
          // Legacy data doesn't have collection context or is from different collection
          console.log('🧹 Clearing invalid legacy group data (wrong/no collection context)');
          localStorage.removeItem(this.storageKey);
        }
      }
    } catch (e) {
      console.warn('Error loading stored group selection:', e);
    }
  }

  saveKitData() {
    if (!this.currentKit) return;
    
    // Get current svsCustomerId from URL or existing localStorage
    const svsCustomerId = this.getCurrentSvsCustomerId();
    
    const kitsData = {
      currentKit: this.currentKit,
      hasGroupedCollection: this.hasGroups,
      svsCustomerId: svsCustomerId, // Persist svsCustomerId with kit data
      collection: this.currentCollection, // Add collection context for validation
      lastUpdated: Date.now()
    };
    
    try {
      // Save enhanced format with collection context
      localStorage.setItem(this.kitsStorageKey, JSON.stringify(kitsData));
      
      // Save legacy format for backwards compatibility (now includes svsCustomerId and collection)
      if (this.selectedGroup) {
        const legacyData = {
          ...this.selectedGroup,
          svsCustomerId: svsCustomerId,
          collection: this.currentCollection // Add collection context to legacy data too
        };
        localStorage.setItem(this.storageKey, JSON.stringify(legacyData));
      }
      
      console.log('✅ Saved kit data with collection context:', kitsData);
    } catch (e) {
      console.error('Error saving kit data:', e);
    }
  }

  storeProductDetails() {
    console.log('🔍 Storing product details for cart use');
    
    try {
      const productDetailsStorage = 'required_products_cache';
      let productCache = {};
      
      // Try to load existing cache
      try {
        const existingCache = localStorage.getItem(productDetailsStorage);
        if (existingCache) {
          productCache = JSON.parse(existingCache);
        }
      } catch (e) {
        console.warn('Error loading existing product cache:', e);
      }
      
      // Store details for all products in the collection
      if (window.collectionGroupData && window.collectionGroupData.products) {
        console.log('🔍 Found', window.collectionGroupData.products.length, 'products to cache');
        
        window.collectionGroupData.products.forEach(product => {
          if (product.id) {
            productCache[product.id.toString()] = {
              id: product.id,
              title: product.title,
              handle: product.handle,
              url: `/products/${product.handle}`,
              price: product.price,
              image: product.featured_image,
              type: product.type,
              cached_at: Date.now()
            };
          }
        });
        
        // Clean up old cache entries (older than 1 hour)
        const oneHourAgo = Date.now() - (60 * 60 * 1000);
        Object.keys(productCache).forEach(productId => {
          if (productCache[productId].cached_at && productCache[productId].cached_at < oneHourAgo) {
            delete productCache[productId];
          }
        });
        
        localStorage.setItem(productDetailsStorage, JSON.stringify(productCache));
        console.log('✅ Stored product details for', Object.keys(productCache).length, 'products');
      } else {
        console.log('❌ No collection product data available to cache');
      }
    } catch (error) {
      console.warn('Error storing product details:', error);
    }
  }

  setupModal() {
    this.setupModalEventListeners();
  }

  setupGroupSelector() {
    this.setupDropdownEventListeners();
    this.showGroupSelector();
  }

  async handleInitialGroupSelection() {
    const urlParams = new URLSearchParams(window.location.search);
    const svsCustomerId = urlParams.get('svsCustomerId');
    
    if (svsCustomerId) {
      // ===== PATH 1: svsCustomerId present - WAIT for API, do NOTHING else =====
      console.log('🛑 svsCustomerId found - BLOCKING all processing until API response received');
      
      this.showLoadingState();
      
      try {
        const customerData = await this.waitForCustomerData(svsCustomerId, 5000);
        
        if (customerData && customerData.group) {
          console.log('✅ API responded with customer data - proceeding with auto-selection');
          this.hideLoadingState();
          this.selectGroupFromCustomerData(customerData);
        } else {
          console.log('❌ API responded but no valid group data - falling back to manual selection');
          this.hideLoadingState();
          this.fallbackToManualSelection();
        }
      } catch (error) {
        console.log('❌ API timeout/error - falling back to manual selection');
        this.hideLoadingState();
        this.fallbackToManualSelection();
      }
      
    } else {
      // ===== PATH 2 & 3: No svsCustomerId - use localStorage or show modal =====
      console.log('📁 No svsCustomerId - checking localStorage');
      
      this.loadStoredData();
      
      if (this.selectedGroup) {
        console.log('✅ Found stored group - using it:', this.selectedGroup.groupName);
        this.updateUI();
      } else {
        console.log('❓ No stored group - showing modal');
        this.showModal();
      }
      
      // Always apply filters and check URL when no svsCustomerId
      this.applyGroupFilter();
      this.checkURLForGroup();
    }
  }

  attemptAutoSelection(customerData, forceOverride = false) {
    if (customerData && customerData.group && (!this.selectedGroup || forceOverride)) {
      const matchingGroup = this.findMatchingGroup(customerData.group);
      
      if (matchingGroup) {
        if (forceOverride && this.selectedGroup) {
          console.log('🎯 Auto-selecting group from customer data (overriding existing selection):', matchingGroup);
          console.log('Previous selection was:', this.selectedGroup);
        } else {
          console.log('🎯 Auto-selecting group from customer data:', matchingGroup);
        }
        
        // Hide loading state before showing final UI
        this.hideLoadingState();
        
        this.selectGroup(matchingGroup.groupKey, matchingGroup.groupName);
        
        // Since we may have been waiting for customer data, ensure filters are applied
        this.applyGroupFilter();
        this.checkURLForGroup();
        return;
      } else {
        console.log('⚠️ Customer group does not match available options:', customerData.group);
        console.log('Available groups:', this.groupDisplayNames);
      }
    }
    
    // Fallback to showing modal and applying filters
    this.hideLoadingState(); // Hide loading state before showing modal
    this.showInitialModalIfNeeded();
    this.applyGroupFilter();
    this.checkURLForGroup();
  }

  showInitialModalIfNeeded() {
    // Show modal if no group is selected
    if (!this.selectedGroup) {
      this.showModal();
    } else {
      this.updateUI();
    }
  }

  setupModalEventListeners() {
    const modal = document.getElementById('groupSelectionModal');
    const groupOptions = document.querySelectorAll('.group-option-btn');
    
    groupOptions.forEach(button => {
      button.addEventListener('click', (e) => {
        const groupKey = e.target.getAttribute('data-group-key');
        const groupName = e.target.getAttribute('data-group-name');
        
        // Check if this is being called from kit warning modal
        const kitWarningModal = document.getElementById('kitWarningModal');
        const isFromKitWarning = kitWarningModal && kitWarningModal.style.display === 'flex';
        
        if (isFromKitWarning) {
          // Update the "new kit" name in the warning modal
          const newKitGroupName = document.getElementById('newKitGroupName');
          if (newKitGroupName) {
            newKitGroupName.textContent = groupName;
          }
          
          // Store the pending selection
          this.pendingGroupSelection = { groupKey, groupName };
          
          // Hide group selection modal and show kit warning modal
          this.hideModal();
          this.showKitWarningModal();
        } else {
          // Normal selection flow
          this.selectGroup(groupKey, groupName);
        }
      });
    });
  }

  setupDropdownEventListeners() {
    const changeGroupButton = document.getElementById('changeGroupButton');
    
    if (changeGroupButton) {
      changeGroupButton.addEventListener('click', () => {
        this.showGroupSelectionModal();
      });
    }
    
    // Setup kit warning modal listeners
    this.setupKitWarningModalListeners();
  }

  setupKitWarningModalListeners() {
    const clearAndStartBtn = document.getElementById('clearAndStartBtn');
    const cancelKitChangeBtn = document.getElementById('cancelKitChangeBtn');
    
    if (clearAndStartBtn) {
      clearAndStartBtn.addEventListener('click', () => {
        this.clearKitAndStartNew();
      });
    }
    
    if (cancelKitChangeBtn) {
      cancelKitChangeBtn.addEventListener('click', () => {
        this.hideKitWarningModal();
      });
    }
  }

  showGroupSelector() {
    const groupSelectorContainer = document.getElementById('groupSelectorContainer');
    if (groupSelectorContainer && this.selectedGroup) {
      // Hide the group selector if the group name is "default" (case insensitive)
      if (this.selectedGroup.groupName && this.selectedGroup.groupName.toLowerCase() === 'default') {
        groupSelectorContainer.style.display = 'none';
        console.log('🙈 Hiding group selector - group name is "Default"');
      } else {
        groupSelectorContainer.style.display = 'block';
      }
    }
  }

  showGroupSelectionModal() {
    // Check if we have an incomplete kit
    if (this.currentKit && !this.currentKit.complete) {
      // Show kit warning modal instead
      this.showKitWarningModal();
    } else {
      // Show regular group selection modal
      this.showModal();
    }
  }

  showKitWarningModal() {
    const modal = document.getElementById('kitWarningModal');
    const currentKitGroupName = document.getElementById('currentKitGroupName');
    
    if (modal && currentKitGroupName && this.currentKit) {
      currentKitGroupName.textContent = this.currentKit.groupName;
      modal.style.display = 'flex';
    }
  }

  hideKitWarningModal() {
    const modal = document.getElementById('kitWarningModal');
    if (modal) {
      modal.style.display = 'none';
    }
  }

  async clearKitAndStartNew() {
    console.log('Clearing current kit and starting new group selection');
    
    // Clear the entire cart first and wait for it to finish
    await this.clearCart();
    
    // Clear current kit and svsCustomerId
    this.currentKit = null;
    this.selectedGroup = null;
    this.persistedSvsCustomerId = null;
    
    // Clear localStorage (both kit data and svsCustomer data)
    try {
      localStorage.removeItem(this.kitsStorageKey);
      localStorage.removeItem(this.storageKey);
      localStorage.removeItem('svsCustomer'); // Clear svsCustomer data as well
      console.log('Cleared all localStorage data including svsCustomer');
      
      // Hide player card since svsCustomer data is now cleared
      this.hidePlayerCard();
    } catch (e) {
      console.warn('Error clearing localStorage:', e);
    }
    
    // Hide kit warning modal
    this.hideKitWarningModal();
    
    // If we have a pending selection, use it
    if (this.pendingGroupSelection) {
      console.log('Applying pending group selection:', this.pendingGroupSelection);
      this.selectGroup(this.pendingGroupSelection.groupKey, this.pendingGroupSelection.groupName);
      this.pendingGroupSelection = null;
    } else {
      // Show group selection modal
      this.showModal();
    }
  }

  /**
   * Clear the entire cart via /cart/clear.js.
   * Used when switching groups to ensure a clean slate.
   */
  async clearCart() {
    console.log('Clearing entire cart');
    try {
      const response = await fetch('/cart/clear.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      if (response.ok) {
        const updatedCart = await response.json();
        console.log('Cart cleared successfully. Item count:', updatedCart.item_count);

        // Trigger cart update event for any cart drawers/notifications
        document.dispatchEvent(new CustomEvent('cart:updated', {
          detail: { cart: updatedCart }
        }));

        // Publish PUB_SUB event if available (for theme cart components)
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'collection-groups',
            cartData: updatedCart
          });
        }

        this.updateCartUI(updatedCart);
      } else {
        console.error('Failed to clear cart, status:', response.status);
      }
    } catch (error) {
      console.error('Error clearing cart:', error);
    }
  }

  async clearCartItemsForKit(groupName) {
    console.log('Clearing cart items for group:', groupName);
    
    try {
      // Get current cart
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      console.log('Current cart:', cart);
      
      // Find items that belong to this group
      const itemsToRemove = [];
      
      cart.items.forEach(item => {
        // Check for group name in properties
        const itemGroupName = item.properties?._kit_group_name || 
                             item.properties?.kit_group_name;
        
        if (itemGroupName === groupName) {
          itemsToRemove.push({
            id: item.id,
            quantity: 0 // Set to 0 to remove
          });
          console.log('Marking item for removal:', item.title, 'Group:', itemGroupName);
        }
      });
      
      if (itemsToRemove.length === 0) {
        console.log('No items found for group:', groupName);
        return;
      }
      
      console.log('Removing', itemsToRemove.length, 'items from cart');
      
      // Remove items from cart
      const updateResponse = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          updates: itemsToRemove.reduce((acc, item) => {
            acc[item.id] = item.quantity;
            return acc;
          }, {})
        })
      });
      
      if (updateResponse.ok) {
        const updatedCart = await updateResponse.json();
        console.log('Cart cleared successfully. New item count:', updatedCart.item_count);
        
        // Trigger cart update event for any cart drawers/notifications
        document.dispatchEvent(new CustomEvent('cart:updated', {
          detail: { cart: updatedCart }
        }));
        
        // Update cart UI if present
        this.updateCartUI(updatedCart);
      } else {
        console.error('Failed to clear cart items');
      }
      
    } catch (error) {
      console.error('Error clearing cart items for kit:', error);
    }
  }

  updateCartUI(cart) {
    // Update cart count in header
    const cartCountElements = document.querySelectorAll('.cart-count, [data-cart-count]');
    cartCountElements.forEach(element => {
      element.textContent = cart.item_count;
    });
    
    // Update cart drawer if it exists
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
      cartDrawer.renderContents(cart);
    }
    
    // Update cart notification if it exists
    const cartNotification = document.querySelector('cart-notification');
    if (cartNotification && typeof cartNotification.renderContents === 'function') {
      cartNotification.renderContents(cart);
    }
    
    console.log('Cart UI updated');
  }

  showModal() {
    // If there's only one group and it's "default", auto-select it instead of showing modal
    if (this.groups.length === 1 && this.groupDisplayNames.length === 1) {
      const onlyGroupName = this.groupDisplayNames[0];
      if (onlyGroupName && onlyGroupName.toLowerCase() === 'default') {
        console.log('🙈 Only group is "Default" - auto-selecting without modal');
        this.selectGroup(this.groups[0], onlyGroupName);
        return;
      }
    }
    
    const modal = document.getElementById('groupSelectionModal');
    if (modal) {
      modal.style.display = 'flex';
    }
  }

  hideModal() {
    const modal = document.getElementById('groupSelectionModal');
    if (modal) {
      modal.style.display = 'none';
    }
  }

  selectGroup(groupKey, groupName) {
    console.log('Selecting group:', groupKey, groupName);
    
    // Check if we're changing groups and have an incomplete kit
    if (this.currentKit && this.currentKit.groupKey !== groupKey && !this.currentKit.complete) {
      console.log('Changing groups with incomplete kit - this would show warning modal in Phase 2');
      // TODO: Phase 2 - Show warning modal
    }
    
    // Create new kit
    this.currentKit = {
      id: 'kit_' + Date.now(),
      groupKey: groupKey,
      groupName: groupName,
      startedAt: Date.now(),
      complete: false
    };
    
    // Update selectedGroup for backwards compatibility
    this.selectedGroup = {
      groupKey: groupKey,
      groupName: groupName
    };
    
    this.saveKitData();
    this.updateUI();
    this.applyGroupFilter();
    this.hideModal();
    
    // Update cart with svsCustomerId if available
    this.updateCartWithSvsCustomerId();
  }

  updateGroupSelectorDisplay() {
    const nameSpan = document.getElementById('currentGroupName');
    const dropdown = document.getElementById('groupDropdown');
    
    if (nameSpan) {
      if (this.selectedGroup) {
        nameSpan.textContent = this.selectedGroup.groupName;
      } else {
        nameSpan.textContent = `Select ${this.groupDisplayTerm}`;
      }
    }

    // Update dropdown selected state
    if (dropdown) {
      const options = dropdown.querySelectorAll('.group-dropdown-option');
      options.forEach(option => {
        const optionGroupKey = option.getAttribute('data-group-key');
        if (optionGroupKey === this.selectedGroup?.groupKey) {
          option.classList.add('selected');
        } else {
          option.classList.remove('selected');
        }
      });
    }
  }

  applyGroupFilter() {
    if (!this.selectedGroup) return;

    console.log('Applying group filter:', this.selectedGroup);

    // Get current product type filter from URL
    const currentProductTypeFilter = new URLSearchParams(window.location.search).get('filterProductType');
    const decodedProductTypeFilter = currentProductTypeFilter ? decodeURIComponent(currentProductTypeFilter) : null;
    console.log('Raw product type filter:', currentProductTypeFilter);
    console.log('Decoded product type filter:', decodedProductTypeFilter);

    // Get all product type wrappers (both with and without groups for compatibility)
    const allProductWrappers = document.querySelectorAll('.productTypeWrapper');
    console.log('Found product wrappers:', allProductWrappers.length);
    
    allProductWrappers.forEach((wrapper, index) => {
      const wrapperProductType = wrapper.getAttribute('data-filter');
      const hasGroups = wrapper.getAttribute('data-has-groups') === 'true';
      
      console.log(`Wrapper ${index}: type="${wrapperProductType}", hasGroups=${hasGroups}, decodedFilter="${decodedProductTypeFilter}"`);
      
      // If there's a product type filter and it doesn't match this wrapper, hide it
      if (decodedProductTypeFilter && decodedProductTypeFilter !== wrapperProductType) {
        console.log(`Wrapper ${index} hidden due to product type filter mismatch`);
        wrapper.style.display = 'none';
        return;
      }

      // For wrappers without groups, show them normally
      if (!hasGroups) {
        wrapper.style.display = '';
        return;
      }

      // For wrappers with groups, apply group filtering
      const groupProducts = wrapper.getAttribute('data-group-products');
      console.log(`Wrapper ${index} data-group-products:`, groupProducts);
      
      if (!groupProducts) {
        console.log(`Wrapper ${index} has no group products data, hiding`);
        wrapper.style.display = 'none';
        return;
      }

      // Parse group products data: "group1:handle1,group1:handle2,unassigned:handle3"
      const productData = groupProducts.split(',');
      console.log(`Wrapper ${index} parsed product data:`, productData);
      
      const groupMatches = productData.filter(item => {
        const [group] = item.split(':');
        return group === this.selectedGroup.groupKey || group === 'unassigned';
      });

      console.log(`Wrapper "${wrapperProductType}": ${groupMatches.length} products match group "${this.selectedGroup.groupKey}"`, groupMatches);

      if (groupMatches.length > 0) {
        wrapper.style.display = '';
        
        // Get the handles that should be visible for this group
        const visibleHandles = groupMatches.map(match => {
          const [, handle] = match.split(':');
          return handle;
        });
        
        console.log(`Visible handles for "${wrapperProductType}":`, visibleHandles);
        
        // Hide/show individual products within the wrapper
        const productItems = wrapper.querySelectorAll('li.grid__item');
        let visibleCount = 0;
        
        productItems.forEach(item => {
          // Look for the product handle in various possible locations
          let productHandle = null;
          
          // Check for handle in card-product forms or links
          const cardForm = item.querySelector('form[action*="/cart/add"]');
          const cardLink = item.querySelector('a[href*="/products/"]');
          
          if (cardForm) {
            // Extract handle from form action
            const action = cardForm.getAttribute('action');
            if (action && action.includes('/products/')) {
              productHandle = action.split('/products/')[1]?.split('?')[0];
            }
          } else if (cardLink) {
            // Extract handle from product link
            const href = cardLink.getAttribute('href');
            if (href && href.includes('/products/')) {
              productHandle = href.split('/products/')[1]?.split('?')[0];
            }
          }
          
          if (productHandle) {
            const shouldShow = visibleHandles.includes(productHandle);
            item.style.display = shouldShow ? '' : 'none';
            if (shouldShow) visibleCount++;
          }
        });
        
        console.log(`"${wrapperProductType}" wrapper: ${visibleCount} products visible after filtering`);
        
        // Hide the entire wrapper if no products are visible
        if (visibleCount === 0) {
          wrapper.style.display = 'none';
        }
      } else {
        wrapper.style.display = 'none';
      }
    });

    // Update required items filtering
    this.updateRequiredItemsForGroup();
  }

  updateRequiredItemsForGroup() {
    console.log('Updating required items for group:', this.selectedGroup.groupKey);
    
    // Handle the special Required Items image/button section
    const requiredItemsImages = document.querySelectorAll('.required-items-image-container');
    const requiredItemsProducts = document.querySelectorAll('.required-items-products-container');
    
    // Check if we're currently showing Required Items (from URL parameter)
    const currentProductTypeFilter = new URLSearchParams(window.location.search).get('filterProductType');
    const decodedProductTypeFilter = currentProductTypeFilter ? decodeURIComponent(currentProductTypeFilter) : null;
    const showingRequiredItems = decodedProductTypeFilter === 'Required Items';
    
    if (showingRequiredItems) {
      // Hide the image/button, show the products
      requiredItemsImages.forEach(container => {
        container.style.display = 'none';
      });
      requiredItemsProducts.forEach(container => {
        container.style.display = '';
      });
    } else {
      // Show the image/button, hide the products
      requiredItemsImages.forEach(container => {
        container.style.display = '';
      });
      requiredItemsProducts.forEach(container => {
        container.style.display = 'none';
      });
    }
  }

  updateURL() {
    if (!this.selectedGroup) return;
    
    const url = new URL(window.location);
    url.searchParams.set('groupKey', this.selectedGroup.groupKey);
    window.history.replaceState({}, '', url);
  }

  // Method to get URL with group parameter maintained
  getURLWithGroup(baseUrl) {
    const url = new URL(baseUrl, window.location.origin);
    if (this.selectedGroup) {
      url.searchParams.set('groupKey', this.selectedGroup.groupKey);
    }
    return url.toString();
  }

  // Method to get URL with both group and svsCustomerId maintained
  getURLWithGroupAndCustomer(baseUrl) {
    const url = new URL(baseUrl, window.location.origin);
    
    if (this.selectedGroup) {
      url.searchParams.set('groupKey', this.selectedGroup.groupKey);
    }
    
    const svsCustomerId = this.getCurrentSvsCustomerId();
    if (svsCustomerId) {
      url.searchParams.set('svsCustomerId', encodeURIComponent(svsCustomerId));
    }
    
    return url.toString();
  }

  checkURLForGroup() {
    const urlParams = new URLSearchParams(window.location.search);
    const groupKeyFromURL = urlParams.get('groupKey');
    
    if (groupKeyFromURL && this.groups.includes(groupKeyFromURL)) {
      const groupIndex = this.groups.indexOf(groupKeyFromURL);
      const groupName = this.groupDisplayNames[groupIndex];
      this.selectGroup(groupKeyFromURL, groupName);
    }
  }

  // Public method to get current group for external use
  getCurrentGroup() {
    return this.selectedGroup;
  }

  // Public method to handle product type filter changes
  onProductTypeFilterChange() {
    if (this.hasGroups && this.selectedGroup) {
      this.applyGroupFilter();
    }
  }

  // Get required products for a specific group
  getRequiredProductsForGroup(groupKey) {
    if (!groupKey || !window.collectionGroupData) return [];
    
    console.log('Getting required products for group:', groupKey);
    
    // Find all product cards that belong to the specified group AND are required items
    const requiredItemsWrapper = document.querySelector('.productTypeWrapper[data-filter="Required Items"][data-has-groups="true"]');
    if (!requiredItemsWrapper) {
      console.log('No required items wrapper found');
      return [];
    }
    
    const groupProducts = requiredItemsWrapper.getAttribute('data-group-products');
    if (!groupProducts) {
      console.log('No group products data found');
      return [];
    }
    
    console.log('Group products data:', groupProducts);
    
    // Parse group products to find handles that belong to the specified group
    const productData = groupProducts.split(',');
    const groupHandles = [];
    
    productData.forEach(item => {
      const [group, handle] = item.split(':');
      if (group === groupKey || group === 'unassigned') {
        groupHandles.push(handle);
      }
    });
    
    console.log('Handles for group:', groupHandles);
    
    // Find product cards with matching handles and extract their product IDs
    const requiredProductIds = [];
    const productCards = requiredItemsWrapper.querySelectorAll('.card-wrapper[data-product-handle]');
    
    productCards.forEach(card => {
      const handle = card.getAttribute('data-product-handle');
      const productId = card.getAttribute('data-product-id');
      
      if (groupHandles.includes(handle) && productId) {
        requiredProductIds.push(productId);
        console.log('Found required product:', handle, '→', productId);
      }
    });
    
    console.log('Final required product IDs for group', groupKey, ':', requiredProductIds);
    return requiredProductIds;
  }

  // Set up dynamic required products for unassigned products
  setupDynamicRequiredProducts() {
    // Find all product forms for unassigned products
    const unassignedForms = document.querySelectorAll('form[data-type="add-to-cart-form"]');
    
    unassignedForms.forEach(form => {
      const needsDynamicValidation = form.querySelector('input[name="properties[needs_dynamic_validation]"]');
      if (!needsDynamicValidation) return;
      
      // This is an unassigned product that needs dynamic required products
      form.addEventListener('submit', (e) => {
        if (!this.selectedGroup) return;
        
        // Get required products for the currently selected group
        const requiredProducts = this.getRequiredProductsForGroup(this.selectedGroup.groupKey);
        
        // Update the hidden fields
        const selectedGroupInput = form.querySelector('.js-selected-group-key');
        const dynamicRequiredInput = form.querySelector('.js-dynamic-required-products');
        
        if (selectedGroupInput) {
          selectedGroupInput.value = this.selectedGroup.groupKey;
        }
        
        if (dynamicRequiredInput) {
          dynamicRequiredInput.value = requiredProducts.join(',');
        }
        
        console.log('Setting dynamic required products for unassigned product:', {
          selectedGroup: this.selectedGroup.groupKey,
          requiredProducts: requiredProducts
        });
      });
    });
  }

  // Add method to get current kit info
  getCurrentKit() {
    return this.currentKit;
  }

  // Add method to mark kit as complete (for future use)
  markKitComplete() {
    if (this.currentKit) {
      this.currentKit.complete = true;
      this.saveKitData();
      console.log('Kit marked as complete:', this.currentKit);
    }
  }

  // Update cart attributes with svsCustomerId and previously purchased products
  updateCartWithSvsCustomerId() {
    const svsCustomerId = this.getCurrentSvsCustomerId();
    const svsCustomerData = this.getSvsCustomerData();
    
    if (svsCustomerId) {
      console.log('Updating cart attributes with svsCustomerId:', svsCustomerId);
      
      // Prepare cart attributes object
      const cartAttributes = {
        'svsCustomerId': svsCustomerId
      };
      
      // Add previously purchased product IDs if available
      if (svsCustomerData && svsCustomerData.purchased_products && Array.isArray(svsCustomerData.purchased_products)) {
        // Convert array to JSON string for cart attribute
        cartAttributes['previously_purchased_products'] = JSON.stringify(svsCustomerData.purchased_products);
        console.log('Adding previously purchased products to cart attributes:', svsCustomerData.purchased_products);
      }
      
      fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          attributes: cartAttributes
        })
      })
      .then(response => response.json())
      .then(data => {
        console.log('Cart updated with svsCustomerId and purchased products attributes');
      })
      .catch((error) => {
        console.error('Error updating cart with svsCustomerId:', error);
      });
    }
    
    // Cart interception is already set up in init(), just ensure it's ready
  }

  /**
   * Set up cart interception to automatically add svsCustomer line item properties
   */
  setupSvsCustomerCartInterception() {
    // Check if we're on a collection page or if we should still intercept
    console.log('🚀 Setting up svsCustomer cart interception');
    console.log('🏪 Has groups:', this.hasGroups);
    console.log('🏪 Current collection:', this.currentCollection);
    const originalFetch = window.fetch;
    const self = this;
    
    window.fetch = function(url, options = {}) {
      const fetchContext = this;
      console.log('🔍 Fetch intercepted:', url);

      // Intercept cart add requests
      if (url.includes('/cart/add')) {
        console.log('🛒 Cart add detected, checking for svsCustomer data...');
        const svsCustomerData = self.getSvsCustomerData();

        console.log('📋 svsCustomerData found:', svsCustomerData);

        // Extract _collection from the request body (set by Liquid from product metafield)
        const incomingCollection = self.extractCollectionFromBody(options.body);
        console.log('📋 Incoming _collection from form:', incomingCollection);

        // Cross-collection validation: check if cart already has items from a different collection
        if (incomingCollection) {
          return originalFetch.call(fetchContext, '/cart.js')
            .then(r => r.json())
            .then(cart => {
              const existingCollections = cart.items
                .map(item => item.properties?._collection)
                .filter(Boolean);

              if (existingCollections.length > 0) {
                const mismatch = existingCollections.find(c => c !== incomingCollection);
                if (mismatch) {
                  console.warn('🚫 Cross-collection cart add blocked:', mismatch, 'vs', incomingCollection);
                  // Pass the pending add-to-cart details so user can clear & retry in one click
                  const pendingAdd = { url, options, svsCustomerData, originalFetch, fetchContext };
                  self.showCrossCollectionError(mismatch, incomingCollection, pendingAdd);
                  return Promise.reject(new Error('Your cart contains items from a different team shop. Please complete that order or clear your cart to continue.'));
                }
              }

              // Collection check passed — inject remaining properties and proceed
              if (svsCustomerData && svsCustomerData.svsCustomerId) {
                self.injectSvsCustomerProperties(options, svsCustomerData);
              }
              return originalFetch.call(fetchContext, url, options);
            })
            .catch(error => {
              // If the cross-collection check itself was the rejection, re-throw it
              if (error.message?.includes('different team shop')) {
                return Promise.reject(error);
              }
              // Otherwise the /cart.js fetch failed — proceed with add-to-cart gracefully
              console.warn('⚠️ Cross-collection check failed, proceeding with cart add:', error);
              if (svsCustomerData && svsCustomerData.svsCustomerId) {
                self.injectSvsCustomerProperties(options, svsCustomerData);
              }
              return originalFetch.call(fetchContext, url, options);
            });
        }

        // No collection on form — still inject svsCustomer properties if available
        if (svsCustomerData && svsCustomerData.svsCustomerId) {
          self.injectSvsCustomerProperties(options, svsCustomerData);
        } else {
          console.log('❌ No valid svsCustomer data - skipping property addition');
        }
      }

      return originalFetch.call(fetchContext, url, options);
    };

    console.log('🔗 SVS Customer cart interception set up for line item properties');
  }

  /**
   * Inject svsCustomer properties and attributes into the cart add request body
   */
  injectSvsCustomerProperties(options, svsCustomerData) {
    if (!svsCustomerData || !svsCustomerData.svsCustomerId) return;

    console.log('✅ Valid svsCustomer data - adding line item properties and cart attributes');
    console.log('📦 Request body type:', typeof options.body, options.body?.constructor?.name);

    if (options.body instanceof FormData) {
      console.log('📝 Processing FormData');
      this.addSvsCustomerPropertiesToFormData(options.body, svsCustomerData);
      this.addSvsCustomerAttributesToFormData(options.body, svsCustomerData);
    } else if (options.body instanceof URLSearchParams) {
      console.log('📝 Processing URLSearchParams');
      const data = {};
      for (let [key, value] of options.body.entries()) {
        data[key] = value;
      }
      this.addSvsCustomerPropertiesToData(data, svsCustomerData);
      this.addSvsCustomerAttributesToData(data, svsCustomerData);
      options.body = new URLSearchParams(data);
    } else if (typeof options.body === 'string') {
      console.log('📝 Processing string body');
      try {
        const jsonData = JSON.parse(options.body);
        if (jsonData.items && Array.isArray(jsonData.items)) {
          console.log('📝 Processing bulk add JSON');
          jsonData.items.forEach(item => {
            if (!item.properties) item.properties = {};
            this.addSvsCustomerPropertiesToItemProperties(item.properties, svsCustomerData);
          });
          if (!jsonData.attributes) jsonData.attributes = {};
          this.addSvsCustomerAttributesToObject(jsonData.attributes, svsCustomerData);
          options.body = JSON.stringify(jsonData);
        }
      } catch (jsonError) {
        try {
          console.log('📝 Processing URLSearchParams string');
          const params = new URLSearchParams(options.body);
          const data = {};
          for (let [key, value] of params.entries()) {
            data[key] = value;
          }
          this.addSvsCustomerPropertiesToData(data, svsCustomerData);
          this.addSvsCustomerAttributesToData(data, svsCustomerData);
          options.body = new URLSearchParams(data).toString();
        } catch (urlError) {
          console.warn('❌ Could not parse cart add body for svsCustomer properties');
        }
      }
    } else {
      console.log('❓ Unknown body type:', typeof options.body);
    }
  }

  /**
   * Show cross-collection error to the user.
   * If pendingAdd is provided, the "Clear Cart" button will clear the cart
   * and automatically retry the blocked add-to-cart request.
   */
  showCrossCollectionError(existingCollection, newCollection, pendingAdd) {
    // Remove any existing cross-collection error
    const existing = document.querySelector('.cross-collection-error');
    if (existing) existing.remove();

    const self = this;
    const errorDiv = document.createElement('div');
    errorDiv.className = 'cross-collection-error';
    errorDiv.setAttribute('role', 'alert');
    errorDiv.innerHTML = `
      <div style="position:fixed;top:0;left:0;right:0;z-index:9999;padding:16px;background:#fef2f2;border-bottom:2px solid #ef4444;text-align:center;">
        <p style="margin:0 0 8px;color:#991b1b;font-weight:600;">Your cart contains items from a different team shop.</p>
        <p style="margin:0 0 12px;color:#991b1b;">Please complete that order or clear your cart to continue shopping here.</p>
        <button class="cross-collection-clear-btn" style="padding:8px 20px;background:#ef4444;color:white;border:none;border-radius:4px;cursor:pointer;margin-right:8px;">Clear Cart & Add Item</button>
        <button class="cross-collection-dismiss-btn" style="padding:8px 20px;background:white;color:#991b1b;border:1px solid #ef4444;border-radius:4px;cursor:pointer;">Dismiss</button>
      </div>
    `;
    document.body.appendChild(errorDiv);

    // Clear cart and replay the add-to-cart
    errorDiv.querySelector('.cross-collection-clear-btn').addEventListener('click', () => {
      const btn = errorDiv.querySelector('.cross-collection-clear-btn');
      btn.disabled = true;
      btn.textContent = 'Clearing cart...';

      fetch('/cart/clear.js', { method: 'POST' })
        .then(() => {
          if (pendingAdd) {
            // Inject svsCustomer properties into the original request before replaying
            if (pendingAdd.svsCustomerData?.svsCustomerId) {
              self.injectSvsCustomerProperties(pendingAdd.options, pendingAdd.svsCustomerData);
            }
            return pendingAdd.originalFetch.call(pendingAdd.fetchContext, pendingAdd.url, pendingAdd.options);
          }
        })
        .then(() => {
          errorDiv.remove();
          location.reload();
        })
        .catch(err => {
          console.error('Error clearing cart and retrying add:', err);
          errorDiv.remove();
          location.reload();
        });
    });

    errorDiv.querySelector('.cross-collection-dismiss-btn').addEventListener('click', () => {
      errorDiv.remove();
    });
  }

  /**
   * Extract _collection value from a cart add request body (set by Liquid hidden input)
   */
  extractCollectionFromBody(body) {
    try {
      if (body instanceof FormData) {
        return body.get('properties[_collection]') || null;
      } else if (body instanceof URLSearchParams) {
        return body.get('properties[_collection]') || null;
      } else if (typeof body === 'string') {
        try {
          const json = JSON.parse(body);
          // Single item add
          if (json.properties?._collection) return json.properties._collection;
          // Bulk add — use first item's collection
          if (json.items?.[0]?.properties?._collection) return json.items[0].properties._collection;
        } catch {
          const params = new URLSearchParams(body);
          return params.get('properties[_collection]') || null;
        }
      }
    } catch (error) {
      console.warn('⚠️ Could not extract _collection from request body:', error);
    }
    return null;
  }

  /**
   * Add svsCustomer properties to FormData
   */
  addSvsCustomerPropertiesToFormData(formData, svsCustomerData) {
    let propertiesAdded = [];

    if (svsCustomerData.lastName && svsCustomerData.lastName.trim()) {
      formData.append('properties[Last Name]', svsCustomerData.lastName.trim());
      propertiesAdded.push('Last Name');
    }

    if (svsCustomerData.jerseyNumber !== null && svsCustomerData.jerseyNumber !== undefined) {
      const jerseyStr = String(svsCustomerData.jerseyNumber).trim();
      if (jerseyStr && jerseyStr !== 'null' && jerseyStr !== 'undefined') {
        formData.append('properties[Jersey Number]', jerseyStr);
        propertiesAdded.push('Jersey Number');
      }
    }

    if (svsCustomerData.svsCustomerId) {
      formData.append('properties[_svsCustomerId]', svsCustomerData.svsCustomerId);
      propertiesAdded.push('_svsCustomerId');
    }

    if (propertiesAdded.length > 0) {
      console.log('✅ Added svsCustomer properties to FormData:', propertiesAdded);
    }
  }

  /**
   * Add svsCustomer properties to data object (for URLSearchParams)
   */
  addSvsCustomerPropertiesToData(data, svsCustomerData) {
    let propertiesAdded = [];

    if (svsCustomerData.lastName && svsCustomerData.lastName.trim()) {
      data['properties[Last Name]'] = svsCustomerData.lastName.trim();
      propertiesAdded.push('Last Name');
    }

    if (svsCustomerData.jerseyNumber !== null && svsCustomerData.jerseyNumber !== undefined) {
      const jerseyStr = String(svsCustomerData.jerseyNumber).trim();
      if (jerseyStr && jerseyStr !== 'null' && jerseyStr !== 'undefined') {
        data['properties[Jersey Number]'] = jerseyStr;
        propertiesAdded.push('Jersey Number');
      }
    }

    if (svsCustomerData.svsCustomerId) {
      data['properties[_svsCustomerId]'] = svsCustomerData.svsCustomerId;
      propertiesAdded.push('_svsCustomerId');
    }

    if (propertiesAdded.length > 0) {
      console.log('✅ Added svsCustomer properties to data object:', propertiesAdded);
    }
  }

  /**
   * Add svsCustomer properties to item properties object (for bulk adds)
   */
  addSvsCustomerPropertiesToItemProperties(properties, svsCustomerData) {
    if (svsCustomerData.lastName && svsCustomerData.lastName.trim()) {
      properties['Last Name'] = svsCustomerData.lastName.trim();
    }

    if (svsCustomerData.jerseyNumber !== null && svsCustomerData.jerseyNumber !== undefined) {
      const jerseyStr = String(svsCustomerData.jerseyNumber).trim();
      if (jerseyStr && jerseyStr !== 'null' && jerseyStr !== 'undefined') {
        properties['Jersey Number'] = jerseyStr;
      }
    }

    if (svsCustomerData.svsCustomerId) {
      properties['_svsCustomerId'] = svsCustomerData.svsCustomerId;
    }
  }

  /**
   * Add svsCustomer cart attributes to FormData
   */
  addSvsCustomerAttributesToFormData(formData, svsCustomerData) {
    let attributesAdded = [];

    if (svsCustomerData.svsCustomerId) {
      formData.append('attributes[svsCustomerId]', svsCustomerData.svsCustomerId);
      attributesAdded.push('svsCustomerId');
    }

    if (svsCustomerData.purchased_products && Array.isArray(svsCustomerData.purchased_products)) {
      formData.append('attributes[previously_purchased_products]', JSON.stringify(svsCustomerData.purchased_products));
      attributesAdded.push('previously_purchased_products');
    }

    if (attributesAdded.length > 0) {
      console.log('✅ Added svsCustomer cart attributes to FormData:', attributesAdded);
    }
  }

  /**
   * Add svsCustomer cart attributes to data object (for URLSearchParams)
   */
  addSvsCustomerAttributesToData(data, svsCustomerData) {
    let attributesAdded = [];

    if (svsCustomerData.svsCustomerId) {
      data['attributes[svsCustomerId]'] = svsCustomerData.svsCustomerId;
      attributesAdded.push('svsCustomerId');
    }

    if (svsCustomerData.purchased_products && Array.isArray(svsCustomerData.purchased_products)) {
      data['attributes[previously_purchased_products]'] = JSON.stringify(svsCustomerData.purchased_products);
      attributesAdded.push('previously_purchased_products');
    }

    if (attributesAdded.length > 0) {
      console.log('✅ Added svsCustomer cart attributes to data object:', attributesAdded);
    }
  }

  /**
   * Add svsCustomer cart attributes to object (for JSON requests)
   */
  addSvsCustomerAttributesToObject(attributes, svsCustomerData) {
    let attributesAdded = [];

    if (svsCustomerData.svsCustomerId) {
      attributes['svsCustomerId'] = svsCustomerData.svsCustomerId;
      attributesAdded.push('svsCustomerId');
    }

    if (svsCustomerData.purchased_products && Array.isArray(svsCustomerData.purchased_products)) {
      attributes['previously_purchased_products'] = JSON.stringify(svsCustomerData.purchased_products);
      attributesAdded.push('previously_purchased_products');
    }

    if (attributesAdded.length > 0) {
      console.log('✅ Added svsCustomer cart attributes to object:', attributesAdded);
    }
  }

  // Get current collection handle from URL
  getCurrentCollectionHandle() {
    const path = window.location.pathname;
    const match = path.match(/\/collections\/([^\/]+)/);
    return match ? match[1] : 'default';
  }

  // Check if stored data belongs to current collection
  isValidForCurrentCollection(data) {
    if (!data || typeof data !== 'object') return false;
    
    // If data has no collection context, it's legacy data - validate it
    if (!data.collection) {
      console.log('⚠️ Found legacy data without collection context - treating as invalid for safety');
      return false;
    }
    
    // Check if collection matches
    const isValid = data.collection === this.currentCollection;
    if (!isValid) {
      console.log(`❌ Data collection mismatch: stored="${data.collection}", current="${this.currentCollection}"`);
    }
    return isValid;
  }

  // Keep existing methods but update to use new data structure
  updateUI() {
    if (!this.selectedGroup) return;
    
    const currentGroupName = document.getElementById('currentGroupName');
    if (currentGroupName) {
      currentGroupName.textContent = this.selectedGroup.groupName;
    }
    
    const groupSelectorContainer = document.getElementById('groupSelectorContainer');
    if (groupSelectorContainer) {
      // Hide the group selector if the group name is "default" (case insensitive)
      if (this.selectedGroup.groupName && this.selectedGroup.groupName.toLowerCase() === 'default') {
        groupSelectorContainer.style.display = 'none';
        console.log('🙈 Hiding group selector in updateUI - group name is "Default"');
      } else {
        groupSelectorContainer.style.display = 'block';
      }
    }
    
    // Hide change group button if svsCustomerId exists for current collection
    this.updateChangeGroupButtonVisibility();
    
    console.log('UI updated for group:', this.selectedGroup.groupName);
    if (this.currentKit) {
      console.log('Current kit:', this.currentKit);
    }
  }

  updateChangeGroupButtonVisibility() {
    const changeGroupButton = document.getElementById('changeGroupButton');
    if (!changeGroupButton) return;
    
    // Check if we have a valid svsCustomerId for the current collection
    const svsCustomerData = this.getSvsCustomerData();
    const hasSvsCustomerId = svsCustomerData && svsCustomerData.svsCustomerId;
    
    if (hasSvsCustomerId) {
      // Hide the change group button - group should be auto-applied from API
      changeGroupButton.style.display = 'none';
      console.log('🔒 Change group button hidden - svsCustomerId present for current collection');
    } else {
      // Show the change group button - user can manually change groups
      changeGroupButton.style.display = 'block';
      console.log('🔓 Change group button shown - no svsCustomerId, manual selection allowed');
    }
  }

  getSvsCustomerData() {
    try {
      const svsCustomerJson = localStorage.getItem('svsCustomer');
      if (svsCustomerJson) {
        const svsCustomerData = JSON.parse(svsCustomerJson);
        
        // Use same validation logic as cart.js - only validate collection context if we're on a collection page
        const path = window.location.pathname;
        const match = path.match(/\/collections\/([^\/]+)/);
        const currentCollection = match ? match[1] : null;
        
        if (currentCollection && svsCustomerData.collection) {
          // We're on a collection page AND have stored collection data - validate they match
          if (svsCustomerData.collection === currentCollection) {
            console.log('✅ Found valid svsCustomer data for current collection:', svsCustomerData);
            return svsCustomerData;
          } else {
            console.log('🧹 svsCustomer data is for different collection - clearing');
            localStorage.removeItem('svsCustomer');
          }
        } else {
          // Either not on a collection page OR no stored collection data - just use what we have
          if (svsCustomerData.svsCustomerId) {
            console.log('✅ Using svsCustomer data (not on collection page or no collection context):', svsCustomerData);
            return svsCustomerData;
          }
        }
      }
    } catch (error) {
      console.error('Error parsing svsCustomer data:', error);
    }

    // Fallback: check URL param directly (NOT via getCurrentSvsCustomerId to avoid infinite recursion)
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const urlId = urlParams.get('svsCustomerId');
      if (urlId && urlId !== 'null') {
        const decoded = decodeURIComponent(urlId);
        console.log('⚠️ No localStorage data — using svsCustomerId from URL for cart attributes:', decoded);
        return { svsCustomerId: decoded };
      }
    } catch (e) {}

    return null;
  }

  getCurrentSvsCustomerId() {
    // First check URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    let svsCustomerId = urlParams.get('svsCustomerId');
    
    if (svsCustomerId && svsCustomerId !== 'null') {
      return decodeURIComponent(svsCustomerId);
    }
    
    // If not in URL, check localStorage svsCustomer data
    const svsCustomerData = this.getSvsCustomerData();
    if (svsCustomerData && svsCustomerData.svsCustomerId) {
      return svsCustomerData.svsCustomerId;
    }
    
    // If not in svsCustomer, check kit data
    try {
      const kitsData = localStorage.getItem(this.kitsStorageKey);
      if (kitsData) {
        const parsed = JSON.parse(kitsData);
        if (parsed.svsCustomerId) {
          return parsed.svsCustomerId;
        }
      }
    } catch (e) {
      console.warn('Error reading svsCustomerId from kit data:', e);
    }
    
    // Finally check legacy storage
    try {
      const legacyData = localStorage.getItem(this.storageKey);
      if (legacyData) {
        const parsed = JSON.parse(legacyData);
        if (parsed.svsCustomerId) {
          return parsed.svsCustomerId;
        }
      }
    } catch (e) {
      console.warn('Error reading svsCustomerId from legacy data:', e);
    }
    
    return null;
  }

  // Wait for customer data to be fetched and stored
  waitForCustomerData(svsCustomerId, timeout = 5000) {
    return new Promise((resolve, reject) => {
      // DO NOT check existing data - always wait for fresh API response
      // This ensures we get the correct data for the current svsCustomerId
      console.log('🔄 Waiting for fresh API response, ignoring any cached customer data');
      
      // Set up timeout
      const timeoutId = setTimeout(() => {
        document.removeEventListener('svsCustomerDataLoaded', eventHandler);
        reject(new Error('Timeout waiting for customer data'));
      }, timeout);
      
      // Listen for the customer data loaded event
      const eventHandler = (event) => {
        clearTimeout(timeoutId);
        if (event.detail && event.detail.svsCustomerId === svsCustomerId) {
          console.log('🎯 Received fresh customer data for svsCustomerId:', svsCustomerId);
          resolve(event.detail);
        } else {
          console.log('❌ Received customer data for different svsCustomerId:', event.detail?.svsCustomerId, 'expected:', svsCustomerId);
          reject(new Error('Received customer data does not match expected svsCustomerId'));
        }
      };
      
      document.addEventListener('svsCustomerDataLoaded', eventHandler, { once: true });
    });
  }

  // Show loading state while waiting for customer data
  showLoadingState() {
    console.log('Showing loading state for customer data fetch');
    
    // Hide the group selector until we know what to show
    const groupSelectorContainer = document.getElementById('groupSelectorContainer');
    if (groupSelectorContainer) {
      groupSelectorContainer.style.display = 'none';
    }
    
    // Show a loading indicator (you can customize this)
    const loadingIndicator = document.createElement('div');
    loadingIndicator.id = 'customerDataLoading';
    loadingIndicator.className = 'customer-data-loading';
    loadingIndicator.innerHTML = `
      <div style="text-align: center; padding: 2rem; color: #6d7175;">
        <div style="margin-bottom: 1rem;">
          <div class="loading-spinner" style="display: inline-block; width: 20px; height: 20px; border: 2px solid #e1e3e5; border-top: 2px solid #2563eb; border-radius: 50%; animation: spin 1s linear infinite;"></div>
        </div>
        <p style="margin: 0; font-size: 0.9rem;">Loading your information...</p>
      </div>
    `;
    
    // Add CSS for spinner animation if not already present
    if (!document.querySelector('#spinner-styles')) {
      const style = document.createElement('style');
      style.id = 'spinner-styles';
      style.textContent = `
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `;
      document.head.appendChild(style);
    }
    
    // Insert loading indicator where group selector would be
    const mainContent = document.querySelector('.page-width') || document.querySelector('main');
    if (mainContent) {
      mainContent.insertBefore(loadingIndicator, mainContent.firstChild);
    }
  }

  // Hide loading state
  hideLoadingState() {
    console.log('Hiding loading state');
    const loadingIndicator = document.getElementById('customerDataLoading');
    if (loadingIndicator) {
      loadingIndicator.remove();
    }
  }

  // Helper method: Select group from customer data (svsCustomerId path)
  selectGroupFromCustomerData(customerData) {
    console.log('🎯 Selecting group from customer data:', customerData.group);
    
    const matchingGroup = this.findMatchingGroup(customerData.group);
    
    if (matchingGroup) {
      // IMPORTANT: Use the customer's original group name as the display name
      // The customer data is the source of truth for the proper display text
      // We only use matchingGroup.groupKey for functionality, but customerData.group for display
      this.selectGroup(matchingGroup.groupKey, customerData.group);
      this.applyGroupFilter();
      this.checkURLForGroup();
      // Update button visibility after selecting group from customer data
      this.updateChangeGroupButtonVisibility();
    } else {
      console.log('⚠️ Customer group does not match available options:', customerData.group);
      console.log('Available groups:', this.groupDisplayNames);
      this.fallbackToManualSelection();
    }
  }

  // Helper method: Fallback to manual selection (when API fails)
  fallbackToManualSelection() {
    console.log('🔄 Falling back to manual selection');
    
    this.loadStoredData();
    
    if (this.selectedGroup) {
      console.log('✅ Found stored group as fallback');
      this.updateUI();
      this.applyGroupFilter();
      this.checkURLForGroup();
    } else {
      console.log('❓ No stored group - showing modal');
      this.showModal();
    }
  }

  // Helper method: Hide player card when customer data is cleared
  hidePlayerCard() {
    console.log('🎭 Hiding player card - no customer data available');
    
    const playerCardSection = document.getElementById('PlayerCardSectionInline');
    if (playerCardSection) {
      playerCardSection.style.display = 'none';
      console.log('✅ Player card hidden');
    } else {
      console.log('⚠️ Player card section not found');
    }
  }

  findMatchingGroup(targetGroupName) {
    // Exact match only (case-insensitive)
    for (let i = 0; i < this.groups.length; i++) {
      const groupKey = this.groups[i];
      const groupName = this.groupDisplayNames[i];
      
      if (groupName && groupName.toLowerCase() === targetGroupName.toLowerCase()) {
        return { groupKey, groupName };
      }
    }
    
    console.log('🔍 Available groups:', this.groups);
    console.log('🔍 Available display names:', this.groupDisplayNames);
    console.log('🔍 Target group:', targetGroupName);
    
    return null;
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', async function() {
  window.collectionGroupsManager = new CollectionGroupsManager();
  
  // Check URL for group parameter after initialization
  if (window.collectionGroupsManager.hasGroups) {
    window.collectionGroupsManager.checkURLForGroup();
    
    // Update required items buttons to maintain group selection
    const requiredItemsButtons = document.querySelectorAll('.required-items-button');
    requiredItemsButtons.forEach(button => {
      button.addEventListener('click', function(e) {
        e.preventDefault();
        
        const currentUrl = new URL(window.location);
        currentUrl.searchParams.set('filterProductType', 'Required Items');
        
        // Maintain group selection and svsCustomerId
        if (window.collectionGroupsManager.selectedGroup) {
          currentUrl.searchParams.set('groupKey', window.collectionGroupsManager.selectedGroup.groupKey);
        }
        
        // Maintain svsCustomerId
        const svsCustomerId = window.collectionGroupsManager.getCurrentSvsCustomerId();
        if (svsCustomerId) {
          currentUrl.searchParams.set('svsCustomerId', encodeURIComponent(svsCustomerId));
        }
        
        // Navigate to the URL
        window.location.href = currentUrl.toString();
      });
    });
    
    // Set up dynamic required products for unassigned products
    window.collectionGroupsManager.setupDynamicRequiredProducts();
  }
});

// Handle browser back/forward buttons
window.addEventListener('popstate', function() {
  if (window.collectionGroupsManager?.hasGroups) {
    window.collectionGroupsManager.checkURLForGroup();
  }
}); 